import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, Settings as SettingsIcon, Play, Key, 
  Trash2, PlusCircle, Save, CheckCircle, HelpCircle, Eye, RefreshCw, Trophy, Sparkles, BookOpen, Clock, Award, Check, Lock
} from 'lucide-react';
import { GDQAppState, GDQEventGame, GDQCategoryType, GDQPuzzle, GDQLeaderboardEntry } from './types';
import { initializeDB, getCurrentEventGame, setCurrentGameId, getTemplates, saveTemplate, deleteTemplate, saveEventGame, getLeaderboard, addLeaderboardEntry, clearLeaderboard, getPuzzleRanking } from './utils/db';
import { playTick, playBuzzer, playSuccessChime, playFanfare } from './utils/synthesizer';
import { ImageCropper } from './components/ImageCropper';
import { VirtualKeyboard } from './components/VirtualKeyboard';
import { ALL_BIBLE_BOOKS, SVG_WEDDING_PUZZLE_1, SVG_WEDDING_PUZZLE_2, SVG_BIRTHDAY_PUZZLE_1, SVG_CHURCH_PUZZLE_1, SVG_BACK_PRESET_1 } from './utils/presets';
import { PRELOADED_VERSES, PreloadedVerseOption } from './utils/preloadedVerses';

// Set standard size constraints for visual kiosk scaling
const GM_CODES = ['1234', '7777', '9999', 'GDQ2026', 'ADMIN'];

export default function App() {
  // Navigation & States
  const [appState, setAppState] = useState<GDQAppState>('INTRO');
  
  // Game Setup & Configurations
  const [activeGame, setActiveGame] = useState<GDQEventGame | null>(null);
  const [templates, setTemplates] = useState<GDQEventGame[]>([]);
  const [currentEditGame, setCurrentEditGame] = useState<Partial<GDQEventGame>>({
    id: '',
    category: 'Wedding',
    celebrants: '',
    place: '',
    date: '',
    contacts: '',
    backgroundImage: SVG_BACK_PRESET_1,
    puzzles: [],
    isTemplate: false
  });

  // active game master category choice
  const [selectedCategory, setSelectedCategory] = useState<GDQCategoryType>('Wedding');

  // active image cropper parameters
  const [cropperData, setCropperData] = useState<{
    rawImage: string;
    puzzleIndex: number;
  } | null>(null);

  // active player parameters
  const [username, setUsername] = useState<string>('');
  const [selectedPuzzle, setSelectedPuzzle] = useState<GDQPuzzle | null>(null);

  // verify verses states
  const [selectedBook, setSelectedBook] = useState<string>('Genesis');
  const [isBookModalOpen, setIsBookModalOpen] = useState<boolean>(false);
  const [selectedChapter, setSelectedChapter] = useState<number>(1);
  const [selectedVerse, setSelectedVerse] = useState<number>(1);
  const [unlockError, setUnlockError] = useState<string>('');

  // active playing board parameters
  const [tiles, setTiles] = useState<number[]>([]);
  const [selectedTile, setSelectedTile] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [moves, setMoves] = useState<number>(0);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [timerActive, setTimerActive] = useState<boolean>(false);
  const timerRef = useRef<any>(null);

  // victory parameters
  const [solvedDuration, setSolvedDuration] = useState<number>(0);
  const [solvedMoves, setSolvedMoves] = useState<number>(0);
  const [finalScore, setFinalScore] = useState<number>(0);
  const [rankInfo, setRankInfo] = useState<{ rank: number; total: number } | null>(null);

  // setup view subroutes
  const [setupMode, setSetupMode] = useState<'LIST' | 'CREATE_FORM'>('LIST');

  // stats / GM dashboard in play view
  const [showStatsModal, setShowStatsModal] = useState<boolean>(false);
  const [statsPasscode, setStatsPasscode] = useState<string>('');
  const [isStatsSessionUnlocked, setIsStatsSessionUnlocked] = useState<boolean>(false);

  // autofill promise template states
  const [autofillTargetIndex, setAutofillTargetIndex] = useState<number | null>(null);
  const [autofillSearchQuery, setAutofillSearchQuery] = useState<string>('');

  // intro splash timers
  const [showIntroButtons, setShowIntroButtons] = useState<boolean>(false);
  const [videoSrc, setVideoSrc] = useState<string>("./assets/videos/intro.mp4");

  // pass code for master settings validation
  const [setupPasscode, setSetupPasscode] = useState<string>('');
  const [setupLoginError, setSetupLoginError] = useState<string>('');
  const [useKeyboardInput, setUseKeyboardInput] = useState<boolean>(false);

  // Bootstrap Local Storage DB
  useEffect(() => {
    initializeDB();
    const game = getCurrentEventGame();
    setActiveGame(game);
    setTemplates(getTemplates());
  }, []);

  // Intro Splash Delay of 4 seconds
  useEffect(() => {
    if (appState === 'INTRO') {
      setShowIntroButtons(false);
      const timer = setTimeout(() => {
        setShowIntroButtons(true);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [appState]);

  // Game Board timer
  useEffect(() => {
    if (timerActive) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds(s => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerActive]);

  // Load game assets
  const setGameAsCurrent = (game: GDQEventGame) => {
    playTick();
    setCurrentGameId(game.id);
    setActiveGame(game);
  };

  // Setup Form Handlers
  const handleEditExistingGame = (gameToEdit: GDQEventGame) => {
    playTick();
    setCurrentEditGame({
      ...gameToEdit,
      isTemplate: gameToEdit.isTemplate || false
    });
    setSetupMode('CREATE_FORM');
  };

  const handleStartCreateGame = (categoryType: GDQCategoryType, templateToAdopt?: GDQEventGame) => {
    const freshId = 'gdq_game_' + Date.now();
    if (templateToAdopt) {
      setCurrentEditGame({
        ...templateToAdopt,
        id: freshId, // force brand new ID to edit locally
        isTemplate: false
      });
    } else {
      setCurrentEditGame({
        id: freshId,
        category: categoryType,
        celebrants: '',
        place: '',
        date: new Date().toISOString().split('T')[0],
        contacts: '',
        backgroundImage: SVG_BACK_PRESET_1,
        puzzles: [
          {
            id: 'pz_' + Date.now() + '_1',
            name: 'First Celebration Puzzle',
            imageUrl: SVG_WEDDING_PUZZLE_1,
            thumbnailUrl: SVG_WEDDING_PUZZLE_1,
            verseBook: 'Genesis',
            verseChapter: 1,
            verseVerse: 1,
            verseText: 'In the beginning, God created the heavens and the earth.',
            thankYouMessage: 'Thank you for celebrating with us! May God bless you.'
          }
        ],
        isTemplate: false
      });
    }
    setSetupMode('CREATE_FORM');
  };

  const handleAddPuzzleBox = () => {
    playTick();
    const currentPuzzles = currentEditGame.puzzles ? [...currentEditGame.puzzles] : [];
    if (currentPuzzles.length >= 10) return; // Cap at 10 puzzles per design

    currentPuzzles.push({
      id: 'pz_' + Date.now() + '_' + (currentPuzzles.length + 1),
      name: `Puzzle ${currentPuzzles.length + 1}`,
      imageUrl: SVG_WEDDING_PUZZLE_2,
      thumbnailUrl: SVG_WEDDING_PUZZLE_2,
      verseBook: 'Genesis',
      verseChapter: 1,
      verseVerse: 1,
      verseText: 'And God saw everything that he had made, and behold, it was very good.',
      thankYouMessage: 'May the peace of Christ accompany you homes.'
    });

    setCurrentEditGame(g => ({ ...g, puzzles: currentPuzzles }));
  };

  const handleRemovePuzzleBox = (idx: number) => {
    playTick();
    const currentPuzzles = currentEditGame.puzzles ? [...currentEditGame.puzzles] : [];
    if (currentPuzzles.length <= 1) return; // Keep at least one puzzle
    currentPuzzles.splice(idx, 1);
    setCurrentEditGame(g => ({ ...g, puzzles: currentPuzzles }));
  };

  const handleSaveSetupGame = (shouldSetAsCurrent: boolean) => {
    playTick();
    if (!currentEditGame.celebrants?.trim()) {
      alert('Gamemaster: Please fill out the Name of Celebrant(s)');
      return;
    }

    const completedGameObj: GDQEventGame = {
      id: currentEditGame.id || 'game_' + Date.now(),
      category: currentEditGame.category || 'Wedding',
      celebrants: currentEditGame.celebrants || 'Celebration',
      place: currentEditGame.place || 'Unknown Fellowship Hall',
      date: currentEditGame.date || '',
      contacts: currentEditGame.contacts || '',
      backgroundImage: currentEditGame.backgroundImage || SVG_BACK_PRESET_1,
      puzzles: currentEditGame.puzzles || [],
      isTemplate: currentEditGame.isTemplate || false
    };

    // Save Event Game in DB
    saveEventGame(completedGameObj);

    if (completedGameObj.isTemplate) {
      saveTemplate(completedGameObj);
    }

    if (shouldSetAsCurrent) {
      setGameAsCurrent(completedGameObj);
    }

    // Refresh configurations
    setTemplates(getTemplates());
    setSetupMode('LIST');
  };

  const handlePresetSelect = (preset: GDQEventGame) => {
    playTick();
    setGameAsCurrent(preset);
  };

  const handleDeleteTemplateObj = (tplId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    playTick();
    if (confirm('Are you sure you want to delete this event setup option offline?')) {
      deleteTemplate(tplId);
      setTemplates(getTemplates());
    }
  };

  // Launch File picker for base64 upload
  const triggerImageUpload = (puzzleIndex: number) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setCropperData({
            rawImage: reader.result as string,
            puzzleIndex: puzzleIndex
          });
        };
        reader.readAsDataURL(file);
      }
    };
    fileInput.click();
  };

  // Image Cropping/Rotator callback
  const handleCroppingDone = (croppedBase: string): void => {
    if (cropperData) {
      const currentPuzzles = currentEditGame.puzzles ? [...currentEditGame.puzzles] : [];
      currentPuzzles[cropperData.puzzleIndex].imageUrl = croppedBase;
      currentPuzzles[cropperData.puzzleIndex].thumbnailUrl = croppedBase;
      
      setCurrentEditGame(g => ({ ...g, puzzles: currentPuzzles }));
      setCropperData(null);
    }
  };

  // Login handler
  const handleSetupLogin = () => {
    playTick();
    if (GM_CODES.includes(setupPasscode)) {
      setSetupPasscode('');
      setSetupLoginError('');
      setAppState('SETUP_DASHBOARD');
      setSetupMode('LIST');
    } else {
      playBuzzer();
      setSetupLoginError('Invalid Gamemaster Access Code. Try again.');
    }
  };

  // Keyboard shortcut listener for Access Portal
  useEffect(() => {
    if (appState === 'SETUP_LOGIN' && !useKeyboardInput) {
      const handleGlobalKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Backspace') {
          playTick();
          setSetupPasscode(p => p.slice(0, -1));
        } else if (e.key === 'Enter') {
          playTick();
          handleSetupLogin();
        } else if (e.key.length === 1) {
          const key = e.key.toUpperCase();
          if (/^[A-Z0-9]$/.test(key)) {
            playTick();
            setSetupPasscode(p => {
              if (p.length < 10) {
                return p + key;
              }
              return p;
            });
          }
        }
      };
      window.addEventListener('keydown', handleGlobalKeyDown);
      return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }
  }, [appState, useKeyboardInput, setupPasscode]);

  // Jigsaw Puzzle Start & Solvers
  const handleInitiatePuzzle = (puzzle: GDQPuzzle) => {
    playTick();
    setSelectedPuzzle(puzzle);
    // Setup initial book selector defaults
    setSelectedBook(puzzle.verseBook || 'Genesis');
    setSelectedChapter(1);
    setSelectedVerse(1);
    setUnlockError('');
    setAppState('PLAY_VERSE_UNLOCK');
  };

  const handleVerifyUnlockVerse = () => {
    if (!selectedPuzzle) return;
    playTick();

    const isMatch = 
      selectedBook.trim().toLowerCase() === selectedPuzzle.verseBook.trim().toLowerCase() &&
      Number(selectedChapter) === Number(selectedPuzzle.verseChapter) &&
      Number(selectedVerse) === Number(selectedPuzzle.verseVerse);

    if (isMatch) {
      playSuccessChime();
      // Initialize 35-pieces grid solver
      const initialSeq = Array.from({ length: 35 }, (_, i) => i);
      // shuffle sequence properly
      let shuffled = [...initialSeq];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setTiles(shuffled);
      setMoves(0);
      setSelectedTile(null);
      setElapsedSeconds(0);
      setTimerActive(true);
      setAppState('PLAY_BOARD');
    } else {
      playBuzzer();
      setUnlockError(`Unlock Code error. Bible reference did not match ${selectedPuzzle.verseBook} ${selectedPuzzle.verseChapter}:${selectedPuzzle.verseVerse}. Feel free to ask a neighbor or investigate again!`);
    }
  };

  const handleTileClick = (index: number) => {
    playTick();
    if (tiles[index] === index) return; // Piece already perfectly aligned!

    if (selectedTile === null) {
      setSelectedTile(index);
    } else {
      if (selectedTile !== index) {
        const newTiles = [...tiles];
        const valA = newTiles[selectedTile];
        const valB = newTiles[index];

        [newTiles[selectedTile], newTiles[index]] = [valB, valA];
        setTiles(newTiles);
        setMoves(m => m + 1);
        setSelectedTile(null);

        // Check if jigsaw is perfectly solved!
        if (newTiles.every((t, i) => t === i)) {
          setTimerActive(false);
          playSuccessChime();
          
          // Calculate score
          const rawDuration = elapsedSeconds;
          const swapCounts = moves + 1;
          const score = Math.max(100, 10000 - (swapCounts * 120) - (rawDuration * 15));

          setSolvedDuration(rawDuration);
          setSolvedMoves(swapCounts);
          setFinalScore(score);

          // Save Leaderboard Entry
          if (activeGame && selectedPuzzle) {
            const added = addLeaderboardEntry({
              gameId: activeGame.id,
              puzzleId: selectedPuzzle.id,
              username: username || 'Anonymous Guest',
              moves: swapCounts,
              duration: rawDuration,
              score: score
            });
            // Fetch rankings info
            const ranks = getPuzzleRanking(selectedPuzzle.id, score);
            setRankInfo(ranks);
          }

          setTimeout(() => {
            playFanfare();
            setAppState('PLAY_VICTORY');
          }, 1500);
        }
      } else {
        setSelectedTile(null);
      }
    }
  };

  const handleRemixJigsaw = () => {
    playTick();
    const initialSeq = Array.from({ length: 35 }, (_, i) => i);
    let shuffled = [...initialSeq];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setTiles(shuffled);
    setSelectedTile(null);
    setMoves(m => m + 1);
  };

  // Game Stats Auth validation
  const handleStatsAuthUnlock = () => {
    playTick();
    if (GM_CODES.includes(statsPasscode)) {
      setIsStatsSessionUnlocked(true);
      setStatsPasscode('');
    } else {
      playBuzzer();
      alert('Wrong code. Exclusive Gamemaster stats portal locked.');
    }
  };

  const handleClearScoreboard = () => {
    playTick();
    if (confirm('Delete all scores and resets player rankings in this event?')) {
      clearLeaderboard();
      alert('Rankings fully reset.');
    }
  };

  return (
    <div className="min-h-screen w-full flex justify-center items-center overflow-hidden" style={{ backgroundColor: '#5c390d' }}>
      <div className="w-full h-screen max-h-screen md:aspect-[9/16] md:max-w-[calc(100vh*9/16)] font-sans text-slate-100 relative select-none shadow-2xl md:border-x md:border-y border-amber-900/50 flex flex-col overflow-hidden" style={{ backgroundColor: '#fff5e8', color: '#5c390d' }}>
      
      {/* 1. INTRO / SPLASH SCREEN */}
      {appState === 'INTRO' && (
        <div className="absolute inset-0 z-30 flex flex-col justify-between pt-[calc(env(safe-area-inset-top,0px)+1.5rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] px-6 transition-all" style={{ backgroundColor: '#fff5e8' }}>
          {/* Looping introductory visual background container */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-35">
            <video 
              src={videoSrc} 
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full h-full object-cover"
              onError={(e) => {
                // If local video fails to load, gracefully degrade by trying the online placeholder video URL
                console.log("No source intro video file found, falling back to beautiful gold celebration loop.");
                const placeholderUrl = "https://assets.mixkit.co/videos/preview/mixkit-slow-motion-of-gold-glitter-shining-40222-large.mp4";
                if (videoSrc !== placeholderUrl) {
                  setVideoSrc(placeholderUrl);
                }
              }}
            />
          </div>
 
          <div className="relative z-10 w-full text-center mt-12 px-2" />
 
          <div className="relative z-10 flex flex-col items-center gap-4 mb-8 px-4">
            {!showIntroButtons ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-[#db8504] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-[#5c390d] font-mono">Initializing tournament engine...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 w-full max-w-sm transition-all animate-in fade-in">
                {/* Big PLAY Button */}
                <button
                  onClick={() => {
                    playTick();
                    setAppState('PLAY_ATTRACT');
                  }}
                  className="w-full font-sans font-black rounded-2xl flex items-center justify-center gap-3 transition shadow-2xl animate-bounce-slow h-16 text-xl border cursor-pointer hover:opacity-95"
                  style={{ backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' }}
                >
                  <Play className="w-6 h-6 fill-[#fff5e8] text-[#fff5e8]" />
                  <span>PLAY QUEST</span>
                </button>
 
                {/* Subdued SET UP Button */}
                <button
                  onClick={() => {
                    playTick();
                    setAppState('SETUP_LOGIN');
                  }}
                  className="text-[#5c390d] hover:text-[#db8504] font-mono text-sm font-bold flex items-center gap-2 border-b border-transparent hover:border-[#db8504]/30 px-3 py-1.5 transition cursor-pointer"
                >
                  <Key className="w-3.5 h-3.5" />
                  <span>GAMEMASTER SETUP</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2. SETUP LOGIN PASSCODE */}
      {appState === 'SETUP_LOGIN' && (
        <div className="absolute inset-0 z-35 flex flex-col justify-between h-full w-full p-0 m-0 overflow-hidden font-sans select-none" style={{ backgroundColor: '#fff5e8' }}>
          {/* Looping introductory visual background container */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-35">
            <video 
              src={videoSrc} 
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full h-full object-cover"
              onError={(e) => {
                // If local video fails to load, gracefully degrade by trying the online placeholder video URL
                console.log("No source intro video file found, falling back to beautiful gold celebration loop.");
                const placeholderUrl = "https://assets.mixkit.co/videos/preview/mixkit-slow-motion-of-gold-glitter-shining-40222-large.mp4";
                if (videoSrc !== placeholderUrl) {
                  setVideoSrc(placeholderUrl);
                }
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#fff5e8] via-[#db8504]/20 to-[#fff5e8]" />
          </div>
 
          {/* Header/Title block positioned cleanly above */}
          <div className="relative z-10 w-full pt-[calc(env(safe-area-inset-top,0px)+2rem)] md:pt-10 px-4 transition-all animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="max-w-xs sm:max-w-sm md:max-w-md mx-auto bg-white/95 border-2 border-[#db8504] rounded-2xl p-5 text-center shadow-2xl backdrop-blur-sm">
              <h2 className="text-3xl md:text-4xl font-cinzel text-[#db8504] font-bold mb-2 tracking-wide leading-tight">ACCESS PORTAL</h2>
              <p className="text-[#5c390d] text-xs sm:text-sm md:text-base font-mono font-medium">
                Enter the Gamemaster passcode.
              </p>
            </div>
          </div>
 
          {/* Main content grid containing the keypad in exactly one cell */}
          <div 
            className="relative z-10 flex-1 grid w-full h-full p-0"
            style={{ 
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', 
              gridTemplateRows: 'repeat(7, minmax(0, 1fr))' 
            }}
          >
            {/* Keypad positioned centered covering columns 2-4 and rows 3-6 (around Sector 18 area) */}
            <div 
              className="flex flex-col justify-center items-center p-2"
              style={{ gridColumn: '2 / 5', gridRow: '3 / 7' }}
            >
              <div className="w-full h-full rounded-2xl p-2 flex flex-col justify-between shadow-2xl backdrop-blur-md relative animate-in zoom-in-95 duration-300 border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504', height: '245.719px' }}>
                <div className="mt-2.5">
                  {/* Passcode display */}
                  <div className="text-center font-mono text-lg py-1.5 rounded-lg text-amber-300 font-bold shadow-inner truncate px-2 select-none h-9 flex items-center justify-center tracking-wider border" style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}>
                    {setupPasscode ? setupPasscode : "••••"}
                  </div>
 
                  {/* Option to toggle input */}
                  <div className="text-center mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        playTick();
                        setUseKeyboardInput(!useKeyboardInput);
                        setSetupPasscode('');
                        setSetupLoginError('');
                      }}
                      className="text-[#fff5e8] hover:text-[#db8504] font-mono text-[8px] underline select-none cursor-pointer tracking-tight"
                    >
                      {useKeyboardInput ? "Click for Numpad" : "Click for Keyboard"}
                    </button>
                  </div>
                </div>
 
                {/* Input container / buttons */}
                {useKeyboardInput ? (
                  <div className="flex flex-col gap-1.5 h-32 justify-center mb-1">
                    <input
                      type="text"
                      autoFocus
                      placeholder="KEYBOARD..."
                      value={setupPasscode}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                        if (val.length <= 10) {
                           setSetupPasscode(val);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSetupLogin();
                        }
                      }}
                      className="w-full text-amber-300 font-mono text-[11px] font-bold rounded-lg text-center py-2 focus:outline-none uppercase tracking-wider border-2"
                      style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}
                    />
                    <button
                      onClick={handleSetupLogin}
                      className="w-full font-bold text-[10px] py-1.5 rounded-lg flex items-center justify-center gap-1 shadow transition border cursor-pointer hover:opacity-95"
                      style={{ backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' }}
                    >
                      <Check className="w-3 h-3 stroke-[3px]" />
                      <span>Submit</span>
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-1 grid-rows-4 h-32 mb-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <button
                        key={num}
                        onClick={() => {
                          playTick();
                          if (setupPasscode.length < 10) setSetupPasscode(p => p + num);
                        }}
                        className="font-mono text-xs font-bold rounded transition flex items-center justify-center border border-slate-200/5 animate-in cursor-pointer hover:opacity-90"
                        style={{ backgroundColor: '#fff5e8', color: '#5c390d' }}
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        playTick();
                        setSetupPasscode('');
                      }}
                      className="font-bold text-[9px] rounded transition flex items-center justify-center border cursor-pointer hover:opacity-90"
                      style={{ backgroundColor: '#fff5e8', color: '#991b1b', borderColor: '#ef4444' }}
                    >
                      CLEAR
                    </button>
                    <button
                      onClick={() => {
                        playTick();
                        if (setupPasscode.length < 10) setSetupPasscode(p => p + '0');
                      }}
                      className="font-mono text-xs font-bold rounded transition flex items-center justify-center border border-slate-200/5 cursor-pointer hover:opacity-90"
                      style={{ backgroundColor: '#fff5e8', color: '#5c390d' }}
                    >
                      0
                    </button>
                    <button
                      onClick={handleSetupLogin}
                      className="font-extrabold text-[9px] rounded flex items-center justify-center transition shadow-md border cursor-pointer hover:opacity-90"
                      style={{ backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' }}
                    >
                      ENTER
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
 
          {/* Footer with error message and back button */}
          <div className="relative z-10 w-full text-center pb-12 px-4 flex flex-col items-center gap-4">
            {setupLoginError && (
              <p className="text-rose-400 font-semibold text-xs bg-rose-950/40 border border-rose-900/50 px-3 py-2 rounded-xl max-w-sm animate-pulse">
                {setupLoginError}
              </p>
            )}
            <button
               onClick={() => {
                 playTick();
                 setSetupPasscode('');
                 setAppState('INTRO');
               }}
               className="text-base font-mono font-bold flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-sm transition-all border shadow-lg cursor-pointer"
               style={{ backgroundColor: '#fff5e8', color: '#5c390d', borderColor: '#5c390d' }}
            >
              <ArrowLeft className="w-4 h-4" /> Back to Splash
            </button>
          </div>
        </div>
      )}
 
      {/* 3. GAMEMASTER DASHBOARD */}
      {appState === 'SETUP_DASHBOARD' && (
        <div className="flex-1 w-full flex flex-col p-8 pt-16 overflow-y-auto" style={{ backgroundColor: '#fff5e8' }}>
          {/* Main header block */}
          <div className="flex flex-col gap-4 bg-slate-900 border border-slate-800 p-6 rounded-3xl mb-8" style={{ backgroundColor: '#5c390d' }}>
            <div>
              <h2 className="text-4xl font-cinzel text-amber-500 font-extrabold mb-1">EVENT MANAGEMENT PORTAL</h2>
              <p className="text-slate-400 text-lg" style={{ color: '#fff5e8' }}>Configure tournaments, calibrate celebration imagery, and manage Bible verse access references.</p>
            </div>
            <button
               onClick={() => {
                 playTick();
                 setAppState('INTRO');
               }}
               className="bg-slate-800 text-amber-500 px-6 py-4 rounded-2xl font-bold hover:bg-slate-700 transition flex items-center justify-center gap-2 border border-slate-700 w-full cursor-pointer hover:opacity-95"
               style={{ minHeight: '60px', backgroundColor: '#db8504', color: '#fff5e8' }}
            >
              <ArrowLeft className="w-5 h-5" /> Quit Setup
            </button>
          </div>
 
          {setupMode === 'LIST' && (
            <div className="flex flex-col gap-8 w-full">
              
              {/* Category selector column */}
              <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 flex flex-col gap-4 h-fit w-full" style={{ backgroundColor: '#5c390d' }}>
                <h3 className="text-xl font-cinzel text-slate-300 font-bold mb-1 uppercase tracking-wider" style={{ color: '#fff5e8' }}>Categories</h3>
                <div className="grid grid-cols-2 gap-2.5 pb-1">
                  {(['Wedding', 'Birthday', 'Graduation', 'Celebration of Life', 'Church', 'School', 'Work', 'Others'] as GDQCategoryType[]).map((cat, idx) => {
                    const isSelected = selectedCategory === cat;
                    
                    let btnStyle: React.CSSProperties = {};
                    let spanStyle: React.CSSProperties = {};
                    let svgStyle: React.CSSProperties = {};
                    let circleStyle: React.CSSProperties = {};
                    let path2Style: React.CSSProperties = {};

                    if (isSelected) {
                      btnStyle = { backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' };
                      spanStyle = { color: '#fff5e8' };
                      svgStyle = { color: '#fff5e8' };
                    } else {
                      btnStyle = { backgroundColor: '#fff5e8', color: '#5c390d', borderColor: '#5c390d' };
                      spanStyle = { color: '#5c390d' };
                      svgStyle = { color: '#5c390d' };
                    }

                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          playTick();
                          setSelectedCategory(cat);
                        }}
                        className="w-full text-left px-3 py-3 rounded-2xl font-bold text-xs sm:text-sm flex justify-between items-center transition border hover:opacity-90 cursor-pointer gap-1.5"
                        style={btnStyle}
                      >
                        <span style={spanStyle} className="leading-snug truncate sm:whitespace-normal">{cat}</span>
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          className="lucide lucide-plus-circle shrink-0"
                          style={svgStyle}
                        >
                          <circle cx="12" cy="12" r="10" style={circleStyle}></circle>
                          <line x1="12" y1="8" x2="12" y2="16"></line>
                          <line x1="8" y1="12" x2="16" y2="12" style={path2Style}></line>
                        </svg>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Event catalog list right */}
              <div className="flex flex-col gap-6 w-full">
                <div className="flex flex-col gap-4 bg-slate-900 border border-slate-800 p-6 rounded-3xl w-full" style={{ maxWidth: '100%', backgroundColor: '#5c390d' }}>
                  <h3 
                    className="text-lg font-cinzel font-bold text-amber-500 tracking-wide uppercase"
                    style={{ color: '#fff5e8' }}
                  >
                    {selectedCategory} EVENT GAMES &amp; TEMPLATES
                  </h3>
                  
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => handleStartCreateGame(selectedCategory)}
                      className="w-full bg-[#db8504] hover:bg-[#db8504]/90 text-[#fff5e8] font-bold px-5 py-4 rounded-2xl flex justify-between items-center transition cursor-pointer"
                    >
                      <span className="text-left font-bold">Create Custom {selectedCategory} Game</span>
                      <PlusCircle className="w-5 h-5 shrink-0" />
                    </button>

                    {templates.filter(t => t.category === selectedCategory).length > 0 && (
                      <div className="bg-slate-950/40 p-4 rounded-2xl border border-white/10 flex flex-col gap-2">
                        <label htmlFor="adopt-template-select" className="text-xs font-mono text-slate-300 font-bold uppercase tracking-wider" style={{ color: '#fff5e8' }}>
                          Adopt an Existing Template:
                        </label>
                        <select
                          id="adopt-template-select"
                          onChange={(e) => {
                            const selectedTplId = e.target.value;
                            if (selectedTplId) {
                              const tpl = templates.find(t => t.id === selectedTplId);
                              if (tpl) {
                                handleStartCreateGame(selectedCategory, tpl);
                              }
                              // reset select value
                              e.target.value = '';
                            }
                          }}
                          className="w-full bg-[#3a2205] border border-[#db8504]/30 rounded-xl px-3 py-2 text-sm text-[#fff5e8] focus:outline-none focus:border-[#db8504]"
                        >
                          <option value="" className="text-[#fff5e8]/60">-- Choose a template to adopt --</option>
                          {templates
                            .filter(t => t.category === selectedCategory)
                            .map(tpl => (
                              <option key={tpl.id} value={tpl.id}>
                                {tpl.celebrants} ({tpl.place})
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Templates rendering */}
                <div className="flex flex-col gap-6 w-full">
                  {templates
                    .filter(t => t.category === selectedCategory)
                    .map((game, index) => {
                      const isActive = activeGame?.id === game.id;
                      return (
                        <div 
                          key={game.id} 
                          className={`border rounded-3xl p-6 flex flex-col justify-between shadow-lg relative overflow-hidden transition-all duration-300 ${
                            isActive ? 'border-[#db8504] ring-2 ring-[#db8504]/20' : 'border-[#db8504]/30'
                          }`}
                          style={{ backgroundColor: '#5c390d' }}
                        >
                          <div className="mb-6">
                            {index === 0 && (
                              <h3 className="text-xl font-cinzel font-extrabold tracking-wider uppercase mb-4" style={{ color: '#fff5e8' }}>
                                CURRENT RUNNING GAME
                              </h3>
                            )}
                            {isActive && index !== 0 && (
                              <div className="inline-block bg-[#db8504] text-[#fff5e8] font-black px-2.5 py-1 rounded-full text-[10px] font-mono mb-2.5 uppercase tracking-wider shadow-sm">
                                ACTIVE RUNNING EVENT
                              </div>
                            )}
                            <span className="text-xs font-mono text-[#fff5e8]/70 block uppercase tracking-widest mb-1">{game.category} • {game.date}</span>
                            <h4 className="text-2xl font-cinzel text-[#fff5e8] font-bold mb-2 leading-tight">{game.celebrants}</h4>
                            <p className="text-[#fff5e8]/85 text-sm">📍 {game.place}</p>
                            <p className="text-[#fff5e8]/85 text-sm">📞 {game.contacts}</p>
                            <p className="text-[#db8504] text-sm font-semibold mt-4">🧩 Contains {game.puzzles.length} customized jigsaw puzzles</p>
                          </div>

                          <div className="flex items-center gap-3">
                            {index !== 0 && (
                              <button
                                onClick={() => setGameAsCurrent(game)}
                                className={`flex-1 py-4 rounded-2xl font-extrabold text-sm transition cursor-pointer ${
                                  isActive 
                                    ? 'bg-[#3a2205] text-emerald-400 border border-emerald-900' 
                                    : 'bg-[#db8504] text-[#fff5e8] hover:bg-[#db8504]/90'
                                }`}
                              >
                                {isActive ? 'CURRENT RUNNING GAME' : 'SET AS CURRENT GAME'}
                              </button>
                            )}
                            
                            {index !== 0 && (
                              <button
                                onClick={() => handleStartCreateGame(selectedCategory, game)}
                                className="bg-[#3a2205] hover:bg-[#3a2205]/80 text-[#fff5e8] py-4 px-4 rounded-2xl font-bold text-sm transition cursor-pointer"
                              >
                                Adopt Template
                              </button>
                            )}

                            {!game.id.startsWith('marriage_anniversary') && !game.id.startsWith('golden_jubilee') && !game.id.startsWith('grace_church_gala') && (
                              <button
                                onClick={(e) => handleDeleteTemplateObj(game.id, e)}
                                className="bg-rose-950/60 hover:bg-rose-900 text-rose-300 p-4 rounded-2xl border border-rose-900/50 transition cursor-pointer"
                                title="Delete Custom Template"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>

                          {index === 0 && (
                            <div className="mt-4 flex flex-col w-full">
                              <button
                                onClick={() => handleEditExistingGame(game)}
                                className="w-full bg-[#db8504] hover:bg-[#db8504]/90 text-[#fff5e8] py-4 px-6 rounded-2xl font-extrabold text-sm transition cursor-pointer flex items-center justify-center gap-2 shadow-md border border-[#db8504]"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-edit"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                <span>Edit Current Running Game</span>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}

                  {templates.filter(t => t.category === selectedCategory).length === 0 && (
                    <div className="col-span-2 text-center p-12 bg-[#5c390d] border border-[#db8504]/30 rounded-3xl">
                      <HelpCircle className="w-16 h-16 text-[#fff5e8]/40 mx-auto mb-4 animate-bounce-slow" />
                      <p className="text-[#fff5e8]/80 text-lg mb-4">No event templates found in this category.</p>
                      <button
                        onClick={() => handleStartCreateGame(selectedCategory)}
                        className="bg-[#db8504]/20 text-[#db8504] px-6 py-3 rounded-full border border-[#db8504]/30 hover:bg-[#db8504]/30 transition text-sm font-bold cursor-pointer"
                      >
                        Create the First Custom Game Template
                      </button>
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}

          {/* Form to Create/Draft New Custom Match */}
          {setupMode === 'CREATE_FORM' && (
            <div className="rounded-3xl p-4 sm:p-8 max-w-4xl mx-auto w-full border-2 animate-in fade-in" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
              <h3 className="text-3xl font-cinzel text-[#db8504] font-extrabold mb-6 border-b border-[#db8504]/20 pb-3">
                DRAFT {selectedCategory.toUpperCase()} EVENT GAME
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Celebrant Field */}
                <div>
                  <label className="block text-[#fff5e8] font-bold mb-2 text-lg">Name of Celebrant(s) *</label>
                  <input
                    type="text"
                    value={currentEditGame.celebrants || ''}
                    onChange={(e) => setCurrentEditGame(g => ({ ...g, celebrants: e.target.value }))}
                    placeholder="e.g. Sarah &amp; Jonathan, David Miller"
                    className="w-full bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-2xl px-5 py-4 focus:outline-none focus:border-[#db8504] transition text-lg"
                  />
                </div>

                {/* Place Field */}
                <div>
                  <label className="block text-[#fff5e8] font-bold mb-2 text-lg">Place of Event</label>
                  <input
                    type="text"
                    value={currentEditGame.place || ''}
                    onChange={(e) => setCurrentEditGame(g => ({ ...g, place: e.target.value }))}
                    placeholder="e.g. Grand Imperial Cathedral"
                    className="w-full bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-2xl px-5 py-4 focus:outline-none focus:border-[#db8504] transition text-lg"
                  />
                </div>

                {/* Date Field */}
                <div>
                  <label className="block text-[#fff5e8] font-bold mb-2 text-lg">Date of Event</label>
                  <input
                    type="date"
                    value={currentEditGame.date || ''}
                    onChange={(e) => setCurrentEditGame(g => ({ ...g, date: e.target.value }))}
                    className="w-full bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-2xl px-5 py-4 focus:outline-none focus:border-[#db8504] transition text-lg font-mono"
                  />
                </div>

                {/* Contacts Field */}
                <div>
                  <label className="block text-[#fff5e8] font-bold mb-2 text-lg">Contacts / Mobile</label>
                  <input
                    type="text"
                    value={currentEditGame.contacts || ''}
                    onChange={(e) => setCurrentEditGame(g => ({ ...g, contacts: e.target.value }))}
                    placeholder="e.g. +1-555-0199"
                    className="w-full bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-2xl px-5 py-4 focus:outline-none focus:border-[#db8504] transition text-lg"
                  />
                </div>

                {/* Event Thumbnail (Square) */}
                <div className="md:col-span-2 flex flex-col sm:flex-row gap-5 items-center bg-[#3a2205] p-5 rounded-3xl border border-[#db8504]/30 mt-2">
                  <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-[#db8504]/50 bg-slate-950/40 overflow-hidden shrink-0 flex items-center justify-center relative shadow-inner">
                    {currentEditGame.eventThumbnail ? (
                      <img src={currentEditGame.eventThumbnail} alt="Event Thumbnail" className="w-full h-full object-cover select-none" />
                    ) : (
                      <Sparkles className="w-8 h-8 text-[#db8504]/30 animate-pulse" />
                    )}
                  </div>
                  <div className="flex-1 w-full text-left">
                    <label className="block text-[#fff5e8] font-bold text-sm mb-1 uppercase tracking-wider">EVENT BRANDING SQUARE THUMBNAIL</label>
                    <div className="flex flex-wrap gap-2.5">
                      <button
                        type="button"
                        onClick={() => {
                          const fileInput = document.createElement('input');
                          fileInput.type = 'file';
                          fileInput.accept = 'image/*';
                          fileInput.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setCurrentEditGame(g => ({ ...g, eventThumbnail: reader.result as string }));
                              };
                              reader.readAsDataURL(file);
                            }
                          };
                          fileInput.click();
                        }}
                        className="bg-[#db8504] hover:bg-[#db8504]/90 text-[#fff5e8] font-bold py-2.5 px-4 rounded-xl text-xs transition cursor-pointer shadow-md"
                      >
                        📷 Choose Logo or Photo
                      </button>
                      {currentEditGame.eventThumbnail && (
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentEditGame(g => ({ ...g, eventThumbnail: undefined }));
                          }}
                          className="bg-rose-950/70 text-rose-300 border border-rose-900/40 hover:bg-rose-900 py-2 px-3.5 rounded-xl text-xs transition cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p className="text-[10px] text-[#fff5e8]/60 mt-1.5 font-mono leading-relaxed">
                      Recommended ratio: 1:1 square. Appears as the centerpiece visual on the Active Game Server scoreboard screen.
                    </p>
                  </div>
                </div>
              </div>

              {/* Accordion Tabs of event puzzles */}
              <div className="border-t border-[#db8504]/20 pt-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <h4 className="text-xl sm:text-2xl font-cinzel text-[#fff5e8] font-bold leading-tight">EVENT PIECES &amp; SOLUTION REFERENCES</h4>
                  <button
                    type="button"
                    onClick={handleAddPuzzleBox}
                    className="bg-[#3a2205] hover:bg-[#3a2205]/90 text-[#db8504] font-bold p-3 sm:p-4 rounded-2xl border border-[#db8504]/30 transition flex flex-row sm:flex-col items-center justify-center text-center cursor-pointer w-full sm:w-auto min-w-[120px] sm:min-w-[130px] gap-2 sm:gap-1 shadow-lg"
                  >
                    <span className="text-2xl sm:text-4xl font-light leading-none select-none">+</span>
                    <div className="flex flex-col items-center leading-tight">
                      <span className="text-xs tracking-wide">Add Puzzle</span>
                      <span className="text-[10px] text-[#fff5e8]/60 font-medium">(Max 10)</span>
                    </div>
                  </button>
                </div>

                <div className="flex flex-col gap-8">
                  {currentEditGame.puzzles?.map((pz, idx) => (
                    <div key={pz.id} className="rounded-3xl p-4 sm:p-6 relative border" style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}>
                      
                      <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-900">
                        <span className="font-mono text-[#db8504] font-bold text-lg">PUZZLE BOX MATCH {idx + 1}</span>
                        {currentEditGame.puzzles!.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemovePuzzleBox(idx)}
                            className="text-rose-400 font-semibold flex items-center gap-1 hover:text-rose-300 text-sm cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4" /> Remove Section
                          </button>
                        )}
                      </div>

                      <div className="flex flex-col gap-6">
                        
                        {/* 5:7 Image cropper segment */}
                        <div className="flex flex-col items-center">
                          <label className="block text-[#fff5e8]/70 font-bold text-sm uppercase self-start mb-2 animate-in">Illustration Source (5:7 ratio)</label>
                          <div className="w-40 h-56 rounded-2xl border-2 border-dashed border-[#db8504]/30 bg-slate-900/40 overflow-hidden flex flex-col justify-center items-center p-1 relative group">
                            {pz.imageUrl ? (
                              <img src={pz.imageUrl} alt="preview" className="w-full h-full object-cover rounded-xl" />
                            ) : (
                              <Sparkles className="w-8 h-8 text-slate-600 animate-pulse" />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => triggerImageUpload(idx)}
                            className="mt-3 bg-[#5c390d] hover:bg-[#5c390d]/90 text-[#db8504] font-bold py-2 px-4 rounded-xl text-[11px] border border-[#db8504]/30 transition w-full text-center cursor-pointer"
                          >
                            📷 Choose &amp; Format Photo
                          </button>
                        </div>

                        {/* Text controls right */}
                        <div className="flex flex-col gap-4">
                          <div>
                            <div className="flex justify-between items-center mb-1.5 flex-wrap gap-2">
                              <label className="block text-[#fff5e8] font-bold text-sm">Name of Puzzle *</label>
                              <button
                                type="button"
                                onClick={() => {
                                  playTick();
                                  setAutofillTargetIndex(idx);
                                  setAutofillSearchQuery('');
                                }}
                                className="bg-[#db8504] hover:bg-[#db8504]/90 text-[#fff5e8] font-bold text-[10px] px-2.5 py-1 rounded-lg transition-all flex items-center gap-1 cursor-pointer border border-[#db8504]"
                              >
                                <span>✨ Autofill Promise &amp; Prayer</span>
                              </button>
                            </div>
                            <input
                              type="text"
                              value={pz.name}
                              onChange={(e) => {
                                const list = [...currentEditGame.puzzles!];
                                list[idx].name = e.target.value;
                                setCurrentEditGame(g => ({ ...g, puzzles: list }));
                              }}
                              className="w-full bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#db8504]"
                              placeholder="e.g. Bridal Joy, David Miller Jubilee Crown"
                            />
                          </div>

                          <div className="border border-[#db8504] bg-[#5c390d]/30 rounded-2xl p-3 sm:p-4">
                            <div className="grid grid-cols-4 gap-2 sm:gap-3 items-end">
                              <div className="col-span-2">
                                <label className="block text-[#fff5e8] font-bold text-sm mb-1 whitespace-nowrap overflow-hidden text-ellipsis">Bible Book</label>
                                <select
                                  value={pz.verseBook}
                                  onChange={(e) => {
                                    const list = [...currentEditGame.puzzles!];
                                    list[idx].verseBook = e.target.value;
                                    setCurrentEditGame(g => ({ ...g, puzzles: list }));
                                  }}
                                  className="w-full h-12 bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-xl px-2 sm:px-4 py-2 focus:outline-none focus:border-[#db8504] text-xs sm:text-sm"
                                >
                                  {ALL_BIBLE_BOOKS.map(b => (
                                    <option key={b} value={b} className="bg-[#5c390d] text-[#fff5e8]">{b}</option>
                                  ))}
                                </select>
                              </div>
                              
                              <div className="col-span-1">
                                <label className="block text-[#fff5e8] font-bold text-sm mb-1 whitespace-nowrap overflow-hidden text-ellipsis">Chapter</label>
                                <input
                                  type="number"
                                  value={pz.verseChapter}
                                  min={1}
                                  onChange={(e) => {
                                    const list = [...currentEditGame.puzzles!];
                                    list[idx].verseChapter = parseInt(e.target.value) || 1;
                                    setCurrentEditGame(g => ({ ...g, puzzles: list }));
                                  }}
                                  className="w-full h-12 bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-xl px-1 py-1 text-center focus:outline-none focus:border-[#db8504] text-xs sm:text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div className="col-span-1">
                                <label className="block text-[#fff5e8] font-bold text-sm mb-1 whitespace-nowrap overflow-hidden text-ellipsis">Verse</label>
                                <input
                                  type="number"
                                  value={pz.verseVerse}
                                  min={1}
                                  onChange={(e) => {
                                    const list = [...currentEditGame.puzzles!];
                                    list[idx].verseVerse = parseInt(e.target.value) || 1;
                                    setCurrentEditGame(g => ({ ...g, puzzles: list }));
                                  }}
                                  className="w-full h-12 bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-xl px-1 py-1 text-center focus:outline-none focus:border-[#db8504] text-xs sm:text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-[#fff5e8] font-bold text-sm mb-1">Verse of Promise (Display Clue)</label>
                            <textarea
                              value={pz.verseText}
                              rows={2}
                              onChange={(e) => {
                                const list = [...currentEditGame.puzzles!];
                                list[idx].verseText = e.target.value;
                                setCurrentEditGame(g => ({ ...g, puzzles: list }));
                              }}
                              className="w-full bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#db8504] resize-none text-sm"
                              placeholder="Type out the Scriptureverse text displayed to the player..."
                            />
                          </div>

                          <div>
                            <label className="block text-[#fff5e8] font-bold text-sm mb-1">Celebrant Message / Prayer (On Victory)</label>
                            <textarea
                              value={pz.thankYouMessage}
                              rows={2}
                              onChange={(e) => {
                                const list = [...currentEditGame.puzzles!];
                                list[idx].thankYouMessage = e.target.value;
                                setCurrentEditGame(g => ({ ...g, puzzles: list }));
                              }}
                              className="w-full bg-[#3a2205] border border-[#db8504]/30 text-[#fff5e8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#db8504] resize-none text-sm"
                              placeholder="Type out custom blessing, thanks or spiritual prayer shown after solve..."
                            />
                          </div>

                        </div>
                      </div>

                    </div>
                  ))}
                </div>
              </div>

              {/* Bot controls template */}
              <div className="flex flex-col gap-4 border-t border-[#db8504]/20 pt-8 mt-10 w-full">
                
                {/* Option save as template */}
                <label className="flex items-center justify-center gap-3 px-6 py-4 rounded-2xl select-none cursor-pointer border w-full" style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}>
                  <input
                    type="checkbox"
                    checked={!!currentEditGame.isTemplate}
                    onChange={(e) => setCurrentEditGame(g => ({ ...g, isTemplate: e.target.checked }))}
                    className="w-6 h-6 rounded accent-[#db8504]"
                  />
                  <span className="text-[#fff5e8] font-bold">Save as a Template</span>
                </label>

                <button
                  type="button"
                  onClick={() => handleSaveSetupGame(true)}
                  className="w-full font-extrabold py-4 px-8 rounded-2xl flex items-center justify-center gap-2 transition border shadow-lg cursor-pointer hover:opacity-95"
                  style={{ backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' }}
                >
                  <Save className="w-5 h-5" />
                  <span>SAVE &amp; LAUNCH GAMEPLAY</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    playTick();
                    setSetupMode('LIST');
                  }}
                  className="w-full font-bold py-4 px-8 rounded-2xl border transition cursor-pointer text-center"
                  style={{ backgroundColor: '#fff5e8', color: '#991b1b', borderColor: '#ef4444' }}
                >
                  Cancel &amp; Go Back
                </button>
              </div>

            </div>
          )}

          {/* Active Image Cropper overlay */}
          {cropperData && (
            <ImageCropper
              imageSrc={cropperData.rawImage}
              onCropComplete={handleCroppingDone}
              onCancel={() => {
                playTick();
                setCropperData(null);
              }}
            />
          )}

        </div>
      )}

      {/* 4. PLAY ATTRACT LOOP */}
      {appState === 'PLAY_ATTRACT' && (
        <div 
          onClick={() => {
            playTick();
            setAppState('PLAY_NAME_INPUT');
          }}
          className="absolute inset-0 bg-slate-950 z-20 flex flex-col justify-between items-center pt-[calc(env(safe-area-inset-top,0px)+1.5rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] px-6 transition-all cursor-pointer overflow-hidden"
          style={{
            backgroundImage: `url(${activeGame?.backgroundImage || SVG_BACK_PRESET_1})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Top header stats option */}
          <div className="relative z-10 w-full flex justify-between items-center pt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                playTick();
                setAppState('INTRO');
              }}
              className="bg-slate-950/80 backdrop-blur px-4 py-2 rounded-xl border border-slate-800 text-slate-400 font-bold text-xs hover:text-amber-500 transition shadow-md"
            >
              ← Back to Splash
            </button>

            <div />
          </div>

          {/* Core Invitation Text */}
          <div className="relative z-10 flex flex-col items-center justify-center text-center max-w-sm w-full bg-white border-2 border-[#db8504] p-7 rounded-3xl shadow-2xl animate-in zoom-in duration-200">
            {activeGame ? (
              <>
                {/* Event Thumbnail displayed elegantly inside the card */}
                {activeGame.eventThumbnail ? (
                  <div className="w-28 h-28 rounded-2xl overflow-hidden border-2 border-[#db8504]/30 shadow-md mb-4 shrink-0 bg-slate-100">
                    <img src={activeGame.eventThumbnail} alt={activeGame.celebrants} className="w-full h-full object-cover select-none" />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-[#fff5e8] flex items-center justify-center border-2 border-dashed border-[#db8504]/40 mb-4 shrink-0">
                    <Sparkles className="w-8 h-8 text-[#db8504]/55" />
                  </div>
                )}

                <span className="font-mono text-[#db8504] font-black text-[10px] uppercase tracking-[0.15em] mb-1.5 block">
                  Welcome to the {activeGame.category} Celebration of
                </span>
                
                <h1 className="text-2xl font-cinzel text-[#5c390d] font-black mb-3 tracking-wide leading-tight break-words">
                  {activeGame.celebrants}
                </h1>
                
                <div className="bg-[#fff5e8] border border-[#db8504]/20 py-2.5 px-4 rounded-xl w-full mb-6">
                  <p className="text-xs text-[#5c390d]/80 font-mono tracking-wider leading-relaxed">
                    📍 {activeGame.place} <br/> 📅 {activeGame.date || 'TBA'}
                  </p>
                </div>

                {/* Play Puzzles Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    playTick();
                    setAppState('PLAY_NAME_INPUT');
                  }}
                  className="w-full max-w-xs mt-3 bg-[#db8504] hover:bg-[#c97503] active:scale-98 transition text-white font-extrabold py-3.5 px-6 rounded-2xl shadow-lg flex items-center justify-center gap-2.5 border-b-4 border-[#a35e02] cursor-pointer"
                >
                  <Play className="w-5 h-5 fill-white" />
                  <span className="text-sm font-sans uppercase tracking-[0.1em]">Play Puzzles</span>
                </button>
              </>
            ) : (
              <div className="text-center p-4 bg-slate-950/90 border border-slate-800 rounded-xl">
                <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-2 animate-bounce-slow" />
                <h3 className="text-lg font-cinzel font-bold mb-1 text-rose-400">NO EVENT LOADED</h3>
                <p className="text-xs text-slate-400 mb-4">Gamemaster, please load a tournament event game to begin!</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    playTick();
                    setAppState('SETUP_LOGIN');
                  }}
                  className="bg-amber-500 text-slate-950 font-bold text-xs px-4 py-2 rounded-xl hover:bg-amber-600 transition"
                >
                  Load Event Game
                </button>
              </div>
            )}
          </div>

          {/* Gamemaster secret score logs access key */}
          <div className="relative z-10 w-full flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                playTick();
                setIsStatsSessionUnlocked(false);
                setStatsPasscode('');
                setShowStatsModal(true);
              }}
              className="bg-slate-950/80 backdrop-blur px-4 py-2 rounded-xl border border-slate-800 text-slate-400 text-xs hover:text-amber-500 transition font-bold"
            >
              🏆 GAME STATS &amp; LEADERBOARD
            </button>
          </div>
        </div>
      )}

      {/* 5. PLAYER USERNAME INPUT */}
      {appState === 'PLAY_NAME_INPUT' && (
        <div className="absolute inset-0 z-25 flex flex-col justify-between items-center pt-[calc(env(safe-area-inset-top,0px)+1rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] px-4 sm:pt-[calc(env(safe-area-inset-top,0px)+1.5rem)] sm:pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] sm:px-6 animate-in fade-in" style={{ backgroundColor: '#fff5e8' }}>
          
          <div className="w-full pt-1 flex justify-between items-center sm:pt-2">
            <button
              onClick={() => {
                playTick();
                setAppState('PLAY_ATTRACT');
              }}
              className="font-bold text-xs px-4 py-2 rounded-xl flex items-center gap-1.5 transition shadow-sm border cursor-pointer hover:bg-white"
              style={{ backgroundColor: '#fff5e8', color: '#5c390d', borderColor: '#db8504' }}
            >
              ← Back
            </button>
            <div className="font-mono text-[9px] text-[#db8504] uppercase font-bold tracking-widest">
              GUEST ACCESS PORTAL
            </div>
          </div>

          <div className="flex-grow flex flex-col items-center justify-center text-center max-w-sm w-full py-4">
            {/* Display the Launcher icon in the upper part of the page */}
            <div className="w-20 h-20 bg-white border-2 border-[#db8504] rounded-2xl flex items-center justify-center p-2 mb-4 shadow-md shrink-0">
              <img
                src="/assets/icons/launcher-icon.png"
                alt="Launcher Icon"
                referrerPolicy="no-referrer"
                className="w-full h-full object-contain rounded-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>

            <h2 className="text-xl font-cinzel font-black text-[#db8504] mb-1 uppercase tracking-wider">
              ENTER QUEST USERNAME
            </h2>
            <p className="text-[#5c390d]/80 text-xs mb-5 font-medium max-w-[280px]">
              Type a username to track your solve speed on the tournament standings:
            </p>

            <div className="w-full border-2 rounded-2xl py-3 px-5 font-mono text-2xl tracking-wider text-center font-bold shadow-inner max-w-xs mx-auto animate-in" style={{ backgroundColor: '#5c390d', borderColor: '#db8504', color: '#fff5e8' }}>
              {username || <span className="text-[#fff5e8]/40 animate-pulse">USERNAME_</span>}
            </div>
          </div>

          {/* Render Standalone Virtual Keyboard */}
          <div className="w-full flex justify-center mb-2">
            <VirtualKeyboard
              value={username}
              onChange={setUsername}
              onEnter={() => {
                if (username.trim()) {
                  playTick();
                  setAppState('PLAY_CATALOGUE');
                }
              }}
              maxLength={12}
            />
          </div>

        </div>
      )}

      {/* 6. PLAY PUZZLE CHOICE CATALOGUE */}
      {appState === 'PLAY_CATALOGUE' && (
        <div className="flex-1 w-full flex flex-col p-6 pt-safe overflow-y-auto animate-in fade-in" style={{ backgroundColor: '#fff5e8' }}>
          {/* Header section catalog */}
          <div className="flex justify-between items-center p-4 rounded-2xl mb-6 border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
            <div>
              <span className="text-[#db8504] font-mono font-bold block text-[10px] tracking-widest uppercase mb-0.5 animate-pulse">
                Let's go, {username}!
              </span>
              <h2 className="text-xl font-cinzel font-black tracking-wide text-[#fff5e8] uppercase leading-none">
                {activeGame?.category || 'Celebration'} PUZZLES
              </h2>
            </div>
            <button
               onClick={() => {
                 playTick();
                 setAppState('PLAY_NAME_INPUT');
               }}
               className="py-2 px-4 rounded-xl font-bold transition text-xs border shadow-md cursor-pointer"
               style={{ backgroundColor: '#fff5e8', color: '#991b1b', borderColor: '#ef4444' }}
            >
              ← Reset
            </button>
          </div>

          {/* Puzzle boxes cards in single beautiful stack suitable for vertical portrait display */}
          <div className="grid grid-cols-1 gap-4 text-left">
            {activeGame?.puzzles?.map((pz, idx) => (
              <div
                key={pz.id}
                onClick={() => handleInitiatePuzzle(pz)}
                className="rounded-2xl p-4 flex gap-4 cursor-pointer hover:border-[#db8504] active:scale-98 transition shadow-lg relative overflow-hidden items-center border"
                style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}
              >
                {/* Square thumbnail frame */}
                <div className="w-16 h-20 rounded-xl overflow-hidden border shrink-0" style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}>
                  {pz.imageUrl ? (
                    <img src={pz.imageUrl} alt={pz.name} className="w-full h-full object-cover select-none animate-in fade-in" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-cinzel text-[#db8504] font-bold text-xs" style={{ backgroundColor: '#3a2205' }}>
                      P{idx + 1}
                    </div>
                  )}
                </div>

                <div className="flex-1 flex justify-between items-center min-w-0">
                  <div>
                    <span className="text-[10px] font-mono text-[#fff5e8]/80 block uppercase tracking-wide">BOX INDEX {idx + 1}</span>
                    <h3 className="text-sm font-cinzel font-bold text-[#fff5e8] truncate" style={{ color: '#fff5e8' }}>{pz.name}</h3>
                  </div>
                  <div className="w-8 h-8 bg-[#db8504]/10 rounded-full flex items-center justify-center border border-[#db8504]/30 text-[#db8504] shrink-0 ml-2">
                    <Play className="w-3.5 h-3.5 fill-[#db8504] translate-x-0.5" />
                  </div>
                </div>
              </div>
            ))}

            {(!activeGame || activeGame.puzzles.length === 0) && (
              <div className="text-center p-8 rounded-xl border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504', color: '#fff5e8' }}>
                <HelpCircle className="w-12 h-12 text-[#fff5e8]/40 mx-auto mb-2" />
                <p className="text-[#fff5e8]/80 text-sm">No active custom puzzles loaded. Go to set-up to add assets.</p>
              </div>
            )}
          </div>
        </div>
      )}

       {appState === 'PLAY_VERSE_UNLOCK' && selectedPuzzle && (
        <div className="absolute inset-0 z-25 flex flex-col justify-between pt-[calc(env(safe-area-inset-top,0px)+1.5rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] px-6 overflow-hidden animate-in fade-in" style={{ backgroundColor: '#fff5e8' }}>
          
          {/* Header */}
          <div className="w-full flex justify-between items-center mb-4 pt-2">
            <button
              onClick={() => {
                playTick();
                setAppState('PLAY_CATALOGUE');
              }}
              className="font-bold text-xs px-3 py-1.5 rounded-xl transition shadow-md border cursor-pointer"
              style={{ backgroundColor: '#fff5e8', color: '#5c390d', borderColor: '#5c390d' }}
            >
              ← Back
            </button>
            <span className="font-mono font-bold px-3 py-1.5 rounded-full border text-[9px] tracking-widest uppercase" style={{ backgroundColor: '#5c390d', color: '#fff5e8', borderColor: '#db8504' }}>
              BIBLE UNLOCK SYSTEM
            </span>
          </div>

          {/* Central Scripture Presentation */}
          <div className="flex-1 w-full max-w-sm mx-auto flex flex-col items-center justify-center gap-4 mb-4">
            <div className="w-10 h-10 bg-indigo-500/15 text-indigo-300 rounded-full flex items-center justify-center border border-indigo-500/35">
              <BookOpen className="w-5 h-5 text-[#5c390d]" />
            </div>

            <div className="text-center rounded-2xl p-5 shadow-2xl relative w-full border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#fff5e8] text-[#5c390d] font-black px-4 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border border-[#db8504]">
                VERSE OF PROMISE
              </span>
              <p className="text-base font-cinzel italic text-slate-100 font-bold leading-relaxed px-2 mt-2">
                &ldquo;{selectedPuzzle.verseText}&rdquo;
              </p>
            </div>

            {/* Error notifications */}
            {unlockError && (
              <p className="text-rose-400 bg-rose-955/30 border border-rose-900/40 px-3 py-1.5 rounded-xl text-center font-semibold text-xs w-full">
                {unlockError}
              </p>
            )}

            {/* Selection input segments */}
            <div className="grid grid-cols-1 gap-2.5 w-full">
              
              {/* BOOK CELL */}
              <div className="rounded-xl p-3 flex flex-col items-center gap-1 border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
                <span className="text-[10px] font-mono text-[#fff5e8] font-bold uppercase tracking-wider">Bible Book</span>
                <button
                  type="button"
                  onClick={() => setIsBookModalOpen(true)}
                  className="w-full text-white font-semibold text-sm py-2 px-3 rounded-lg text-center border transition cursor-pointer"
                  style={{ backgroundColor: '#3a2205', borderColor: '#db8504', color: '#fff5e8' }}
                >
                  {selectedBook}
                </button>
              </div>

              {/* CHAPTER AND VERSE COLLAPSED HORIZONTALLY */}
              <div className="grid grid-cols-2 gap-2.5 w-full">
                {/* CHAPTER CELL */}
                <div className="rounded-xl p-2.5 flex flex-col items-center gap-1 border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
                  <span className="text-[10px] font-mono text-[#fff5e8] font-bold uppercase tracking-wider">Chapter</span>
                  <div className="flex items-center gap-2 w-full justify-between">
                    <button
                      onClick={() => {
                        playTick();
                        setSelectedChapter(c => Math.max(1, c - 1));
                      }}
                      className="w-8 h-8 rounded-lg font-bold text-sm cursor-pointer flex items-center justify-center border transition"
                      style={{ backgroundColor: '#3a2205', color: '#fff5e8', borderColor: '#db8504' }}
                    >
                      -
                    </button>
                    <span className="font-mono text-base font-bold text-[#fff5e8]">{selectedChapter}</span>
                    <button
                      onClick={() => {
                        playTick();
                        setSelectedChapter(c => c + 1);
                      }}
                      className="w-8 h-8 rounded-lg font-bold text-sm cursor-pointer flex items-center justify-center border transition"
                      style={{ backgroundColor: '#3a2205', color: '#fff5e8', borderColor: '#db8504' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* VERSE CELL */}
                <div className="rounded-xl p-2.5 flex flex-col items-center gap-1 border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
                  <span className="text-[10px] font-mono text-[#fff5e8] font-bold uppercase tracking-wider">Verse</span>
                  <div className="flex items-center gap-2 w-full justify-between">
                    <button
                      onClick={() => {
                        playTick();
                        setSelectedVerse(v => Math.max(1, v - 1));
                      }}
                      className="w-8 h-8 rounded-lg font-bold text-sm cursor-pointer flex items-center justify-center border transition"
                      style={{ backgroundColor: '#3a2205', color: '#fff5e8', borderColor: '#db8504' }}
                    >
                      -
                    </button>
                    <span className="font-mono text-base font-bold text-[#fff5e8]">{selectedVerse}</span>
                    <button
                      onClick={() => {
                        playTick();
                        setSelectedVerse(v => v + 1);
                      }}
                      className="w-8 h-8 rounded-lg font-bold text-sm cursor-pointer flex items-center justify-center border transition"
                      style={{ backgroundColor: '#3a2205', color: '#fff5e8', borderColor: '#db8504' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* VERIFY UNLOCK */}
          <div className="w-full flex justify-center mb-2">
            <button
              onClick={handleVerifyUnlockVerse}
              className="w-full max-w-sm text-[#fff5e8] font-sans font-black tracking-wider rounded-xl flex items-center justify-center gap-2 shadow-2xl transition border-2 h-12 text-sm cursor-pointer hover:opacity-95"
              style={{ backgroundColor: '#db8504', borderColor: '#db8504' }}
            >
              <CheckCircle className="w-4 h-4 text-[#fff5e8]" />
              <span>UNLOCK PUZZLE</span>
            </button>
          </div>

          {/* Book choosing overlays */}
          {isBookModalOpen && (
            <div className="absolute inset-0 bg-slate-950/95 z-40 flex flex-col items-center justify-center p-4">
              <div className="w-full h-full rounded-2xl p-4 flex flex-col overflow-hidden border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
                <h3 className="text-sm font-cinzel text-[#fff5e8] font-bold mb-2 text-center uppercase tracking-wider">CHOOSE BIBLE BOOK</h3>
                
                {/* Massive scrollable grid */}
                <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-1.5 p-1 mb-3">
                  {ALL_BIBLE_BOOKS.map((b) => (
                    <button
                      key={b}
                      onClick={() => {
                        playTick();
                        setSelectedBook(b);
                        setIsBookModalOpen(false);
                      }}
                      className="py-2 rounded-lg font-bold border text-xs transition cursor-pointer"
                      style={{
                        backgroundColor: selectedBook === b ? '#db8504' : '#3a2205',
                        borderColor: '#db8504',
                        color: '#fff5e8'
                      }}
                    >
                      {b}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setIsBookModalOpen(false)}
                  className="py-2.5 rounded-xl text-xs font-bold w-full transition cursor-pointer"
                  style={{ backgroundColor: '#fff5e8', color: '#5c390d' }}
                >
                  Close Options
                </button>
              </div>
            </div>
          )}

        </div>
      )}

       {/* 8. MAIN STANDALONE JIGSAW BOARD */}
      {appState === 'PLAY_BOARD' && selectedPuzzle && (
        <div className="absolute inset-0 z-20 flex flex-col justify-between" style={{ backgroundColor: '#fff5e8' }}>
          
          {/* Header statistics bar */}
          <header className="border-b border-[#db8504]/20 px-4 pb-2 pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] flex justify-between items-center shrink-0 md:pt-2" style={{ backgroundColor: '#5c390d' }}>
            <button
              onClick={() => {
                playTick();
                setTimerActive(false);
                setAppState('PLAY_CATALOGUE');
              }}
              className="font-bold text-xs px-3 py-1.5 rounded-xl flex items-center gap-1.5 border transition cursor-pointer"
              style={{ height: '40px', backgroundColor: '#fff5e8', color: '#991b1b', borderColor: '#ef4444' }}
            >
              ← Quit Jigsaw
            </button>

            <div className="text-center">
              <h2 className="text-sm font-cinzel text-[#fff5e8] font-bold tracking-wider leading-none mb-0.5">
                {selectedPuzzle.name}
              </h2>
              <p className="text-[9px] font-mono text-[#fff5e8]/70 uppercase tracking-wider">
                35 Pieces • SWAP MODE
              </p>
            </div>

            <div className="flex items-center gap-3 px-3 py-1.5 rounded-xl border font-mono text-[11px]" style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}>
              <div className="flex items-center gap-1 text-[#db8504]">
                <Clock className="w-3.5 h-3.5" />
                <span className="font-bold">{elapsedSeconds}s</span>
              </div>
              <div className="text-[#db8504]/40">|</div>
              <div className="flex items-center gap-1 text-[#fff5e8]">
                <Award className="w-4 h-4 text-[#db8504]" />
                <span className="font-bold">{moves}m</span>
              </div>
            </div>
          </header>

          {/* Main jigsaw space content */}
          <main className="flex-1 flex items-center justify-center p-4 relative">
            <div
              className="overflow-hidden shadow-2xl flex flex-wrap content-start rounded-2xl ring-2 ring-indigo-500/20 relative"
              style={{
                width: 'min(90%, calc((100vh - 190px) * 5 / 7))',
                aspectRatio: '5 / 7',
                backgroundColor: '#3a2205'
              }}
            >
              {tiles.map((tileIndex, i) => {
                const originalCol = tileIndex % 5;
                const originalRow = Math.floor(tileIndex / 5);
                const isSelected = selectedTile === i;
                const isCorrect = tileIndex === i;

                return (
                  <div
                    key={i}
                    onClick={() => {
                      if (!isCorrect) {
                        handleTileClick(i);
                      }
                    }}
                    className={`relative select-none border-[1.5px] border-slate-950/20 box-border ${
                      isSelected 
                        ? 'z-10 brightness-125 ring-2 ring-amber-500 shadow-2xl scale-[0.98]' 
                        : isCorrect 
                          ? 'brightness-105 pointer-events-none select-none touch-none'
                          : 'active:scale-95 cursor-pointer filter hover:brightness-105'
                    }`}
                    style={{
                      width: '20%',
                      height: '14.2857%', // 100 / 7 rows
                      backgroundImage: `url(${selectedPuzzle.imageUrl})`,
                      backgroundSize: '500% 700%',
                      backgroundPosition: `${(originalCol / (5 - 1)) * 100}% ${(originalRow / (7 - 1)) * 100}%`,
                      backgroundRepeat: 'no-repeat'
                    }}
                  >
                    {/* Centered transparent padlock icon overlay for fixed pieces */}
                    {isCorrect && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/15 pointer-events-none transition duration-150 animate-in fade-in">
                        <Lock className="w-6 h-6 text-white/50 drop-shadow-md" style={{ color: '#ffffff80' }} />
                      </div>
                    )}
                    
                    {isSelected && (
                      <div className="absolute inset-0 bg-amber-500/25 animate-pulse" />
                    )}
                  </div>
                );
              })}

              {/* Instant high-opacity Reference Preview overlay */}
              {showPreview && (
                <div className="absolute inset-0 z-30 animate-in fade-in duration-100 p-0">
                  <img src={selectedPuzzle.imageUrl} alt="Reference Preview" className="w-full h-full object-cover rounded-2xl select-none" />
                </div>
              )}
            </div>
          </main>

          {/* Touch-friendly floating bottom-right controls area */}
          <footer className="border-t border-white/5 p-3 flex flex-col gap-2.5 select-none shrink-0 z-25" style={{ backgroundColor: '#5c390d' }}>
            <div className="text-[#fff5e8] font-mono text-[9px] leading-snug text-center">
              💡 <span className="font-bold">Kiosk Play Tip:</span> Tap any two out-of-order blocks sequentially to swap their positions on screen!
            </div>

            {/* Ergonomic Touch triggers bottom-right */}
            <div className="flex gap-2.5 justify-center">
              {/* Hold to reveals eye button */}
              <button
                onMouseDown={() => { playTick(); setShowPreview(true); }}
                onMouseUp={() => setShowPreview(false)}
                onMouseLeave={() => setShowPreview(false)}
                onTouchStart={(e) => { e.preventDefault(); playTick(); setShowPreview(true); }}
                onTouchEnd={() => setShowPreview(false)}
                onTouchCancel={() => setShowPreview(false)}
                className="font-bold px-3 py-2 rounded-xl flex items-center justify-center gap-1.5 border transition shadow-sm cursor-pointer select-none active:scale-95 duration-100"
                style={{ height: '42px', minWidth: '120px', backgroundColor: '#fff5e8', color: '#5c390d', borderColor: '#db8504' }}
              >
                <Eye className="w-4 h-4" />
                <span className="text-xs">Hold to View</span>
              </button>

              <button
                onClick={handleRemixJigsaw}
                className="font-bold px-3 py-2 rounded-xl flex items-center justify-center gap-1.5 border transition shadow-sm cursor-pointer select-none active:scale-95 duration-100"
                style={{ height: '42px', minWidth: '120px', backgroundColor: '#fff5e8', color: '#db8504', borderColor: '#db8504' }}
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-xs">Remix Board</span>
              </button>
            </div>
          </footer>

        </div>
      )}

      {/* 9. PLAY VICTORY */}
      {appState === 'PLAY_VICTORY' && selectedPuzzle && (
        <div 
          className="absolute inset-0 z-30 flex flex-col justify-between pt-[calc(env(safe-area-inset-top,0px)+1.5rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] px-6 overflow-hidden animate-in fade-in" 
          style={{ 
            backgroundImage: `url(${selectedPuzzle.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: '#fff5e8' 
          }}
        >
          {/* Tint/overlay mask to darken page bg for depth & high contrast */}
          <div className="absolute inset-0 bg-slate-950/75 backdrop-blur-xs z-0 pointer-events-none" />
          
          {/* Congratulations banner in a beautiful, high-contrast beige container */}
          <div className="relative z-10 w-full max-w-sm mx-auto text-center mt-2 bg-[#fff5e8] border-2 border-[#db8504] p-5 rounded-3xl shadow-2xl animate-in slide-in-from-top duration-300">
            <h1 className="font-serif italic text-3xl md:text-4xl text-[#db8504] font-black tracking-wide select-none leading-none">
              Congratulations!
            </h1>
            <p className="font-mono text-xs text-[#5c390d]/80 uppercase tracking-widest mt-1.5 font-extrabold animate-pulse">
              ...you did it!
            </p>
          </div>

          {/* Celebrants prayer/scroll notes in a beautiful, high-contrast beige container */}
          <div className="relative z-10 flex-grow w-full max-w-sm mx-auto flex flex-col justify-center items-center py-4 text-center">
            <div className="bg-[#fff5e8] border-2 border-[#db8504] p-5 sm:p-6 rounded-3xl shadow-2xl w-full text-center">
              <span className="font-mono text-[9px] uppercase text-[#db8504] font-black tracking-widest block mb-2">
                CELEBRANT'S BLESSING PRAYER
              </span>
              <p className="font-serif italic text-base sm:text-lg leading-relaxed text-[#5c390d] px-2 max-h-56 overflow-y-auto w-full font-medium">
                &ldquo;{selectedPuzzle.thankYouMessage}&rdquo;
              </p>
            </div>
          </div>

          {/* Amen Action Trigger bottom */}
          <div className="relative z-10 w-full flex justify-center mb-4">
            <button
              onClick={() => {
                playTick();
                // Push to scores page showing rank
                setAppState('PLAY_ATTRACT');
                setShowStatsModal(true);
                setIsStatsSessionUnlocked(true); // Auto unlock current stats to inspect rankings immediately
              }}
              className="w-full max-w-sm font-black rounded-2xl flex flex-col items-center justify-center shadow-lg transition h-14 border-2 cursor-pointer hover:opacity-95"
              style={{ backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' }}
            >
              <span className="text-xl font-black uppercase tracking-widest leading-none">AMEN</span>
              <span className="text-[10px] font-mono uppercase tracking-wider mt-0.5" style={{ color: '#fff5e8' }}>See Score &amp; Leaderboard</span>
            </button>
          </div>

        </div>
      )}

      {/* 10. STATISTICS POPUP MODAL (Leaderboard & GM score list) */}
      {showStatsModal && (
        <div className="absolute inset-0 bg-slate-950/95 z-35 flex items-center justify-center pt-[calc(env(safe-area-inset-top,0px)+1rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] px-4 select-none">
          <div className="w-full h-full rounded-2xl p-4 flex flex-col shadow-2xl overflow-hidden border" style={{ backgroundColor: '#5c390d', borderColor: '#db8504' }}>
            
            {/* Header statistics portal */}
            <div className="flex justify-between items-center pb-3 border-b border-indigo-900 mb-4 shrink-0">
              <div>
                <h3 className="text-sm font-cinzel text-[#db8504] font-semibold tracking-wide uppercase leading-none">CELEBRATION STANDINGS</h3>
                <p className="text-[#fff5e8]/60 text-[8px] font-mono uppercase tracking-wider mt-0.5">Event Scoreboard</p>
              </div>
              <button
                onClick={() => {
                  playTick();
                  setShowStatsModal(false);
                  setIsStatsSessionUnlocked(false);
                  setStatsPasscode('');
                }}
                className="px-3 py-1.5 rounded-lg text-[10px] transition font-bold border cursor-pointer"
                style={{ backgroundColor: '#fff5e8', color: '#991b1b', borderColor: '#ef4444' }}
              >
                Close Panel
              </button>
            </div>

            {/* If statistics blocked - request GM access code or player unlock */}
            {!isStatsSessionUnlocked ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
                <HelpCircle className="w-10 h-10 text-slate-300 mb-2 animate-bounce-slow" />
                <h4 className="text-sm font-cinzel text-slate-100 font-bold mb-1">GAMEMASTER LOG CONTROL</h4>
                <p className="text-slate-200 text-xs mb-4 max-w-xs">Please submit the Gamemaster Passcode to access tournament logs and rankings:</p>

                <div className="flex flex-col gap-2 mb-4 w-full max-w-xs">
                  <input
                    type="password"
                    value={statsPasscode}
                    onChange={(e) => setStatsPasscode(e.target.value)}
                    placeholder="Enter Code..."
                    className="w-full text-center font-bold tracking-widest text-sm border bg-[#3a2205] text-[#fff5e8] rounded-lg px-3 py-2 focus:outline-none"
                    style={{ borderColor: '#db8504' }}
                  />
                  <button
                    onClick={handleStatsAuthUnlock}
                    className="w-full py-2 rounded-lg font-bold text-xs border cursor-pointer hover:opacity-95"
                    style={{ backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' }}
                  >
                    Unlock Scoreboard
                  </button>
                  <button
                    onClick={() => {
                      playTick();
                      setShowStatsModal(false);
                      setIsStatsSessionUnlocked(false);
                      setStatsPasscode('');
                    }}
                    className="w-full py-2 rounded-lg font-bold text-xs border cursor-pointer hover:opacity-95 text-center"
                    style={{ backgroundColor: '#fff5e8', color: '#991b1b', borderColor: '#ef4444' }}
                  >
                    Exit Log Control
                  </button>
                </div>

                <p className="text-[10px] text-[#fff5e8]/50 italic">Scores are stored safely in WebView local storage.</p>
              </div>
            ) : (
              // Scores portal
              <div className="flex-1 flex flex-col overflow-hidden">
                
                {/* Visual score card overview if solved puzzle stats are populated */}
                {finalScore > 0 && selectedPuzzle && (
                  <div className="grid grid-cols-4 gap-2 border p-2.5 rounded-xl mb-3 shrink-0" style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}>
                    <div className="text-center">
                      <span className="text-[8px] text-[#fff5e8]/75 uppercase font-mono block">PLAYER</span>
                      <span className="text-xs font-bold text-[#fff5e8] truncate block">{username || 'Anonymous'}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-[8px] text-[#fff5e8]/75 uppercase font-mono block">TIME</span>
                      <span className="text-xs font-bold text-[#db8504] block">{solvedDuration}s</span>
                    </div>
                    <div className="text-center">
                      <span className="text-[8px] text-[#fff5e8]/75 uppercase font-mono block">SWAPS</span>
                      <span className="text-xs font-bold text-amber-500 block">{solvedMoves}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-[8px] text-[#fff5e8]/75 uppercase font-mono block">SCORE</span>
                      <span className="text-[10px] font-black text-emerald-400 block truncate">
                        {finalScore} pts
                      </span>
                    </div>
                  </div>
                )}

                {/* Score Log table header */}
                <h4 className="text-[10px] font-mono text-[#db8504] mb-2 tracking-wider uppercase shrink-0">Participant Performance Logs</h4>
                
                <div className="flex-1 overflow-y-auto rounded-xl p-2 border" style={{ backgroundColor: '#3a2205', borderColor: '#db8504' }}>
                  {getLeaderboard().length === 0 ? (
                    <div className="text-center py-6 text-[#fff5e8]/50 font-mono text-[10px] leading-relaxed">
                      No participants logged scores yet.<br/>Be the first to attempt the celebration quest!
                    </div>
                  ) : (
                    <table className="w-full text-left font-sans text-xs">
                      <thead>
                        <tr className="border-b border-[#db8504]/20 text-[#fff5e8]/60 text-[8px] font-mono uppercase tracking-wider">
                          <th className="py-2 px-1">RANK</th>
                          <th>PLAYER</th>
                          <th>MOVES</th>
                          <th>TIME</th>
                          <th className="text-right">FAITH SCORE</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#db8504]/10">
                        {getLeaderboard()
                          .sort((a, b) => b.score - a.score)
                          .map((entry, index) => (
                            <tr key={entry.id} className="text-[11px] text-[#fff5e8]">
                              <td className="py-2 px-1 font-mono text-[#db8504] font-semibold">
                                #{index + 1} {index === 0 && '🥇'} {index === 1 && '🥈'} {index === 2 && '🥉'}
                              </td>
                              <td className="font-bold text-[#fff5e8] max-w-[80px] truncate">{entry.username}</td>
                              <td className="font-mono text-[#fff5e8]/80">{entry.moves}</td>
                              <td className="font-mono text-[#fff5e8]/70">{entry.duration}s</td>
                              <td className="font-mono text-right font-black text-emerald-400">{entry.score} pts</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Tournament control buttons */}
                <div className="flex justify-between items-center pt-3 mt-3 border-t border-[#db8504]/20 shrink-0">
                  <button
                    onClick={handleClearScoreboard}
                    className="bg-rose-950/55 hover:bg-rose-900 border border-[#db8504]/10 text-rose-350 px-3 py-1.5 rounded-lg font-mono text-[9px] font-bold flex items-center gap-1 transition cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Reset Logs
                  </button>

                  <button
                    onClick={() => {
                      playTick();
                      setShowStatsModal(false);
                      setIsStatsSessionUnlocked(false);
                      setStatsPasscode('');
                      setAppState('PLAY_ATTRACT'); // Back to starting invitation
                    }}
                    className="px-4 py-1.5 rounded-lg font-bold text-xs transition border cursor-pointer hover:opacity-95"
                    style={{ backgroundColor: '#db8504', color: '#fff5e8', borderColor: '#db8504' }}
                  >
                    Exit Log Control
                  </button>
                </div>

              </div>
            )}

          </div>
        </div>
      )}

      {/* 11. PRELOADED PROMISES & PRAYERS AUTOFILL MODAL */}
      {autofillTargetIndex !== null && (
        <div className="absolute inset-0 bg-slate-950/95 z-40 flex items-center justify-center pt-[calc(env(safe-area-inset-top,0px)+1rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] px-4 select-none animate-in fade-in">
          <div className="w-full h-full max-h-[90vh] md:max-h-[85vh] rounded-2xl p-5 flex flex-col shadow-2xl overflow-hidden border" style={{ backgroundColor: '#fff5e8', borderColor: '#db8504' }}>
            
            {/* Header */}
            <div className="flex justify-between items-center pb-3 border-b border-[#db8504]/25 shrink-0">
              <div>
                <h3 className="text-sm font-cinzel text-[#5c390d] font-bold uppercase leading-none">
                  {(currentEditGame.category || selectedCategory)} Promises
                </h3>
                <p className="text-[#db8504] text-[9px] font-mono uppercase tracking-wider mt-1">
                  Preloaded Templates ({PRELOADED_VERSES[currentEditGame.category || selectedCategory]?.length || 0} Options)
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  playTick();
                  setAutofillTargetIndex(null);
                }}
                className="px-3 py-1.5 rounded-lg text-[10px] transition font-bold border-2 cursor-pointer hover:bg-rose-50"
                style={{ backgroundColor: '#fff', color: '#991b1b', borderColor: '#ef4444' }}
              >
                Close list
              </button>
            </div>

            {/* Filter / Search input */}
            {((currentEditGame.category || selectedCategory) !== 'Others' && PRELOADED_VERSES[currentEditGame.category || selectedCategory]?.length > 0) && (
              <div className="my-3 shrink-0">
                <label className="block text-[10px] font-mono uppercase tracking-wider font-bold text-[#5c390d] mb-1">Search or Filter Scriptures</label>
                <input
                  type="text"
                  value={autofillSearchQuery}
                  onChange={(e) => setAutofillSearchQuery(e.target.value)}
                  placeholder="Filter by Bible book, reference, or keyword text..."
                  className="w-full text-xs font-semibold border bg-white text-[#5c390d] rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#db8504]"
                  style={{ borderColor: '#db8504' }}
                />
              </div>
            )}

            {/* Template list */}
            <div className="flex-grow overflow-y-auto pr-1 flex flex-col gap-3 min-h-0">
              {(() => {
                const cat = currentEditGame.category || selectedCategory;
                const options = PRELOADED_VERSES[cat] || [];
                
                if (cat === 'Others' || options.length === 0) {
                  return (
                    <div className="text-center py-12 px-4 text-[#5c390d]/80 font-semibold text-sm bg-amber-50/75 rounded-2xl border-2 border-dashed border-[#db8504]/30 my-auto flex flex-col items-center justify-center gap-2">
                      <span className="text-3xl">📋</span>
                      <span>No autofill values for this category; please use manual entry.</span>
                    </div>
                  );
                }

                const query = autofillSearchQuery.toLowerCase().trim();
                const filteredOptions = options.filter(opt => {
                  if (!query) return true;
                  return (
                    opt.book.toLowerCase().includes(query) ||
                    String(opt.chapter).includes(query) ||
                    String(opt.verse).includes(query) ||
                    opt.verseText.toLowerCase().includes(query) ||
                    opt.prayer.toLowerCase().includes(query)
                  );
                });

                if (filteredOptions.length === 0) {
                  return (
                    <div className="text-center py-12 text-[#5c390d]/60 font-medium text-xs">
                      No matching preloaded scripture references found.<br />Try another filter keyword.
                    </div>
                  );
                }

                return filteredOptions.map((opt, oIdx) => {
                  // Check if this specific scripture option has been used in this particular event's puzzle boxes
                  const isUsed = currentEditGame.puzzles?.some(p => 
                    p.verseBook?.trim().toLowerCase() === opt.book.trim().toLowerCase() &&
                    Number(p.verseChapter) === Number(opt.chapter) &&
                    Number(p.verseVerse) === Number(opt.verse)
                  );

                  return (
                    <div 
                      key={oIdx} 
                      className={`p-3.5 rounded-xl border transition-all flex flex-col gap-2 relative ${
                        isUsed 
                          ? 'bg-slate-200/60 border-slate-300 opacity-65' 
                          : 'bg-white border-[#db8504]/20 hover:border-[#db8504]/50'
                      }`}
                    >
                      {/* Header item */}
                      <div className="flex justify-between items-start">
                        <span className="font-sans font-extrabold text-xs text-[#5c390d] flex items-center gap-1">
                          📖 {opt.book} {opt.chapter}:{opt.verse}
                        </span>
                        {isUsed ? (
                          <span className="bg-rose-100 text-rose-800 text-[8px] font-mono leading-none px-1.5 py-0.5 rounded font-bold uppercase border border-rose-300">
                            Chosen
                          </span>
                        ) : (
                          <span className="bg-emerald-50 text-emerald-800 text-[8px] font-mono leading-none px-1.5 py-0.5 rounded font-bold uppercase border border-emerald-200">
                            Available
                          </span>
                        )}
                      </div>

                      {/* Verse text */}
                      <p className="text-xs text-[#5c390d]/90 font-serif italic leading-relaxed border-l-2 border-[#db8504]/30 pl-2.5 py-0.5">
                        &ldquo;{opt.verseText}&rdquo;
                      </p>

                      {/* Associated victory prayer */}
                      <div className="p-2 rounded bg-amber-50/50 border border-[#db8504]/10 text-[11px] leading-relaxed">
                        <span className="font-mono text-[8px] uppercase font-black text-[#db8504] tracking-wider block mb-0.5">
                          Victory Prayer / Message
                        </span>
                        <p className="font-serif italic text-[#5c390d]/85">
                          {opt.prayer}
                        </p>
                      </div>

                      {/* Selection Action Button */}
                      <div className="mt-1">
                        <button
                          type="button"
                          disabled={isUsed}
                          onClick={() => {
                            const list = [...(currentEditGame.puzzles || [])];
                            if (list[autofillTargetIndex] !== undefined) {
                              list[autofillTargetIndex].verseBook = opt.book;
                              list[autofillTargetIndex].verseChapter = opt.chapter;
                              list[autofillTargetIndex].verseVerse = opt.verse;
                              list[autofillTargetIndex].verseText = opt.verseText;
                              list[autofillTargetIndex].thankYouMessage = opt.prayer;
                              
                              // Suggest a beautiful puzzle name if empty or generic
                              if (!list[autofillTargetIndex].name || list[autofillTargetIndex].name.startsWith('Puzzle Match') || list[autofillTargetIndex].name === 'Bridal Joy Celebration' || list[autofillTargetIndex].name === 'The Golden Covenant Ring' || list[autofillTargetIndex].name === 'The Crown of Life Jubilee' || list[autofillTargetIndex].name === 'Unity in Worship Fellowship') {
                                list[autofillTargetIndex].name = `${opt.book} ${opt.chapter}:${opt.verse} Promise`;
                              }

                              setCurrentEditGame(g => ({ ...g, puzzles: list }));
                              setAutofillTargetIndex(null);
                              playSuccessChime();
                            }
                          }}
                          className={`w-full py-2 px-3 rounded-lg font-bold text-[10px] transition cursor-pointer text-center ${
                            isUsed 
                              ? 'bg-slate-350 text-slate-500 border border-slate-300 cursor-not-allowed' 
                              : 'bg-[#db8504] hover:bg-[#db8504]/90 text-white border border-[#db8504]'
                          }`}
                        >
                          {isUsed ? 'Already Loaded in this Event Game' : 'Select & Populate to Puzzle Box'}
                        </button>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

          </div>
        </div>
      )}

      </div>
    </div>
  );
}
